import {
  AbstractControl,
  AsyncValidatorFn,
  ValidationErrors,
} from '@angular/forms';
import { Observable, of, timer } from 'rxjs';
import { debounceTime, map, catchError, switchMap } from 'rxjs/operators';
import { CommuneService } from '../services/commune/commune.service';

const slugCache: { slugs: string[] | null; lastUpdated: number } = {
  slugs: null,
  lastUpdated: 0,
};

const CACHE_DURATION = 10 * 60 * 1000; // Cache duration: 10 minutes

// Regex to only allow valid URL characters (lowercase letters, numbers, hyphens)
const VALID_URL_REGEX = /^[a-z0-9-]*$/;

/**
 * Async Validator to verify that a slug:
 * - Is unique
 * - Contains only URL-safe characters
 * - Does not contain whitespace or special characters
 *
 * @param communeService Service to fetch slug data.
 * @param currentSlug Optional: The current slug to exclude during validation.
 */
export function uniqueSlugValidator(
  communeService: CommuneService,
  currentSlug?: string
): AsyncValidatorFn {
  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    const value = control.value?.trim().toLowerCase();

    // Immediately return if the input matches the existing slug
    if (value === currentSlug) {
      return of(null); // Validation passes
    }

    // Return errors for invalid input patterns
    const formatErrors = validateSlugFormat(value);
    if (formatErrors) {
      return of(formatErrors);
    }

    // Check if value is empty
    if (!value) {
      return of({ required: true });
    }

    const now = Date.now();

    // If cached slugs are available and still valid, validate against them
    if (slugCache.slugs && now - slugCache.lastUpdated < CACHE_DURATION) {
      return of(validateUniqueness(value, slugCache.slugs));
    }

    // Debounce API call for slug validation
    return timer(300).pipe(
      switchMap(() =>
        communeService.getSlugs().pipe(
          map(slugs => {
            // Update cache
            slugCache.slugs = slugs;
            slugCache.lastUpdated = now;

            // Validate against fetched slugs
            return validateUniqueness(value, slugs);
          }),
          // Handle errors gracefully
          catchError(() => of(null))
        )
      )
    );
  };
}

/**
 * Validate the format of the slug using regex to enforce URL-safe characters.
 *
 * @param value The slug to validate.
 * @returns ValidationErrors | null
 */
function validateSlugFormat(value: string): ValidationErrors | null {
  // Check for spaces
  if (/\s/.test(value)) {
    return { whitespaceNotAllowed: true };
  }

  // Check for invalid characters
  if (!VALID_URL_REGEX.test(value)) {
    return { noSpecialCharsAllowed: true };
  }

  return null; // No format errors
}

/**
 * Validate the uniqueness of the slug against a list of existing slugs.
 *
 * @param value The slug to validate.
 * @param slugs List of existing slugs to validate against.
 * @returns ValidationErrors | null
 */
function validateUniqueness(
  value: string,
  slugs: string[]
): ValidationErrors | null {
  return slugs.includes(value) ? { notUnique: true } : null;
}
